import { FileSearchOutlined } from "@ant-design/icons";
import { Alert, Select, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useAdmins } from "@/entities/admin";
import type { AuditEntry } from "@/entities/audit";
import { AUDIT_ACTIONS, useAudit } from "@/entities/audit";
import { useAuth } from "@/entities/session";
import { errorMessage } from "@/shared/api";
import { ROUTES, SECTIONS } from "@/shared/config";
import { formatDateTime } from "@/shared/lib";
import { ActionTag, EmptyState, LoadMore, PageHeader, SectionCard } from "@/shared/ui";

/**
 * Журнал действий (AD-06).
 *
 * Фильтр по администраторам доступен только суперадмину — не из соображений
 * секретности, а потому что список учёток ему одному и отдаётся. Сам журнал
 * видят все: смысл аудита в том, что действия видны коллегам.
 */
export function AuditPage() {
  const { t } = useTranslation();
  const { isSuperadmin } = useAuth();
  const [adminId, setAdminId] = useState<number | undefined>(undefined);
  const [actions, setActions] = useState<string[]>([]);

  const admins = useAdmins(isSuperadmin);
  const query = useAudit({ adminId, actions });
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<AuditEntry> = [
    {
      title: t("audit.when"),
      key: "created_at",
      width: 190,
      render: (_, row) => (
        <Typography.Text type="secondary">{formatDateTime(row.created_at)}</Typography.Text>
      ),
    },
    { title: t("audit.admin"), dataIndex: "admin_login", width: 170 },
    {
      title: t("audit.action"),
      key: "action",
      width: 270,
      render: (_, row) => <ActionTag action={row.action} />,
    },
    {
      title: t("audit.target"),
      key: "target",
      width: 200,
      render: (_, row) => {
        if (!row.target_type || !row.target_id) return "—";
        const label = `${row.target_type} #${String(row.target_id)}`;
        // Пользователь — единственная цель, у которой есть свой экран; остальные
        // (заявки, роботы, настройки) живут в списках, и ссылка вела бы в никуда.
        return row.target_type === "user" ? (
          <Link to={ROUTES.user(row.target_id)}>{label}</Link>
        ) : (
          label
        );
      },
    },
    {
      title: t("audit.payload"),
      key: "payload",
      render: (_, row) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {Object.entries(row.payload ?? {})
            // JSON.stringify и для примитивов: `String(value)` на объекте даёт
            // «[object Object]», а payload у разных действий разной формы.
            .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
            .join(", ") || "—"}
        </Typography.Text>
      ),
    },
    {
      title: t("audit.ip"),
      dataIndex: "ip",
      width: 150,
      render: (value: string | null | undefined) => (
        <Typography.Text type="secondary">{value ?? "—"}</Typography.Text>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        section="audit"
        title={t("audit.title")}
        subtitle={t("audit.hint")}
        extra={
          <>
            {isSuperadmin && (
              <Select
                allowClear
                style={{ width: 220 }}
                placeholder={t("audit.allAdmins")}
                value={adminId}
                onChange={setAdminId}
                options={(admins.data ?? []).map((admin) => ({
                  value: admin.id,
                  label: admin.login,
                }))}
              />
            )}
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              style={{ minWidth: 280 }}
              placeholder={t("audit.allActions")}
              value={actions}
              onChange={setActions}
              options={AUDIT_ACTIONS.map((action) => ({
                value: action,
                label: t(`action.${action}`, action),
              }))}
            />
          </>
        }
      />

      {query.isError && (
        <Alert type="error" showIcon title={errorMessage(query.error)} style={{ marginBottom: 16 }} />
      )}

      <SectionCard color={SECTIONS.audit.color} bodyPadding={0}>
        <Table<AuditEntry>
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={query.isPending}
          pagination={false}
          // Ровно столько, чтобы «Детали» оставались читаемыми, а IP не
          // уезжал за край: остальные колонки заданы фиксированно.
          scroll={{ x: 1240 }}
          locale={{
            emptyText: <EmptyState icon={<FileSearchOutlined aria-hidden />} title={t("audit.empty")} />,
          }}
        />
        <LoadMore
          hasMore={query.hasNextPage}
          loading={query.isFetchingNextPage}
          onClick={() => {
            void query.fetchNextPage();
          }}
        />
      </SectionCard>
    </>
  );
}
